// src/components/AgentCard.js
import React, { useState } from 'react';

export default function AgentCard({ agent, status, onRefresh, onEdit, onDelete, onOpenFrame }) {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh(agent);
    setRefreshing(false);
  };

  const isOnline = status?.online;
  const isWAReady = status?.whatsapp_ready;
  const memRSS = status?.memory?.rss || '—';
  const lastTask = status?.last_task;
  const taskRunning = status?.task_running;

  const statusLabel = status === undefined
    ? 'Checking...'
    : !isOnline
      ? 'Offline'
      : isWAReady
        ? 'Connected'
        : 'Disconnected';

  const dotColor = status === undefined
    ? 'bg-slate-500'
    : !isOnline
      ? 'bg-red-500'
      : isWAReady
        ? 'bg-brand-400 status-dot-live'
        : 'bg-yellow-400';

  const domain = agent.domain?.replace(/\/$/, '') || '';

  return (
    <div
      className={`card-glass rounded-2xl p-5 flex flex-col gap-4 transition-all duration-300 hover:border-white/10 ${isWAReady ? 'glow-green' : ''}`}
      style={{ animationFillMode: 'both' }}
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />
            <h3 className="font-display font-600 text-white text-sm truncate">{agent.name}</h3>
          </div>
          <p className="text-slate-500 text-xs font-mono truncate">{domain || 'No domain set'}</p>
        </div>
        <span className={`text-xs font-display font-600 px-2.5 py-1 rounded-lg flex-shrink-0 ${
          statusLabel === 'Connected'
            ? 'bg-brand-400/10 text-brand-400'
            : statusLabel === 'Offline'
              ? 'bg-red-500/10 text-red-400'
              : statusLabel === 'Disconnected'
                ? 'bg-yellow-400/10 text-yellow-400'
                : 'bg-slate-700/50 text-slate-400'
        }`}>
          {statusLabel}
        </span>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Memory" value={isOnline ? memRSS : '—'} />
        <Stat label="Task" value={taskRunning === true ? '🔄 Running' : taskRunning === false ? 'Idle' : '—'} />
        <Stat
          label="Last Task"
          value={lastTask ? new Date(lastTask).toLocaleTimeString('en-BD', { hour: '2-digit', minute: '2-digit' }) : '—'}
        />
      </div>

      {/* Note */}
      {agent.note && (
        <p className="text-slate-500 text-xs leading-relaxed border-l-2 border-slate-700 pl-3">{agent.note}</p>
      )}

      {/* Actions — ২টা row এ ভাগ করা যাতে overflow না হয় */}
      <div className="flex flex-col gap-2 pt-1 border-t border-white/5">

        {/* Row 1 — QR, Status, Health */}
        <div className="flex items-center gap-1.5">
          <ActionBtn
            onClick={() => onOpenFrame(domain + '/qr', agent.name + ' · QR')}
            title="QR"
            icon={
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <path d="M14 14h2v2h-2zM18 14h3M18 18v3M14 18h2M20 20h1v1h-1z"/>
              </svg>
            }
            color="green"
          />
          <ActionBtn
            onClick={() => onOpenFrame(domain + '/login-status', agent.name + ' · Status')}
            title="Status"
            icon={
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
              </svg>
            }
            color="blue"
          />
          <ActionBtn
            onClick={() => window.open(domain + '/health', '_blank')}
            title="Health"
            icon={
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            }
            color="purple"
          />
        </div>

        {/* Row 2 — Refresh, Edit, Delete (icon only, right aligned) */}
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-all disabled:opacity-40"
            title="Refresh status"
          >
            <svg viewBox="0 0 24 24" className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 2v6h-6M3 12a9 9 0 0115-6.7L21 8M3 22v-6h6M21 12a9 9 0 01-15 6.7L3 16"/>
            </svg>
          </button>
          <button
            onClick={() => onEdit(agent)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-all"
            title="Edit"
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button
            onClick={() => onDelete(agent)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/5 transition-all"
            title="Delete"
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
              <path d="M10 11v6M14 11v6M9 6V4h6v2"/>
            </svg>
          </button>
        </div>

      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
      <p className="text-slate-600 text-xs font-display uppercase tracking-wider mb-0.5">{label}</p>
      <p className="text-white text-xs font-mono truncate">{value}</p>
    </div>
  );
}

function ActionBtn({ onClick, title, icon, color }) {
  const colors = {
    green: 'hover:bg-brand-400/10 hover:text-brand-400',
    blue: 'hover:bg-blue-400/10 hover:text-blue-400',
    purple: 'hover:bg-purple-400/10 hover:text-purple-400',
  };
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-slate-400 text-xs font-display font-500 transition-all duration-150 ${colors[color]}`}
    >
      {icon}
      {title}
    </button>
  );
}